import { deriveKey, encryptString, decryptString } from './crypto'

const DB_NAME = 'byokapp-keys'
const DB_VERSION = 1
const STORE_NAME = 'keys'

interface StoredKeyRecord {
  provider: string
  label: string
  lastFour: string
  iv: number[]
  ciphertext: number[]
  salt: number[]
}

export interface KeyMeta {
  provider: string
  label: string
  lastFour: string
}

function openDb(idbFactory: IDBFactory = indexedDB): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = idbFactory.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME, { keyPath: 'provider' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbGet(db: IDBDatabase, provider: string): Promise<StoredKeyRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).get(provider)
    req.onsuccess = () => resolve(req.result as StoredKeyRecord | undefined)
    req.onerror = () => reject(req.error)
  })
}

function idbPut(db: IDBDatabase, record: StoredKeyRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const req = tx.objectStore(STORE_NAME).put(record)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

function idbDelete(db: IDBDatabase, provider: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const req = tx.objectStore(STORE_NAME).delete(provider)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

function idbGetAll(db: IDBDatabase): Promise<StoredKeyRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).getAll()
    req.onsuccess = () => resolve(req.result as StoredKeyRecord[])
    req.onerror = () => reject(req.error)
  })
}

export class KeyStore {
  private readonly passphrase: string
  private readonly idbFactory: IDBFactory

  constructor(passphrase: string, idbFactory?: IDBFactory) {
    this.passphrase = passphrase
    this.idbFactory = idbFactory ?? indexedDB
  }

  async saveKey(provider: string, label: string, rawKey: string): Promise<void> {
    const salt = crypto.getRandomValues(new Uint8Array(16))
    const encKey = await deriveKey(this.passphrase, salt)
    const { iv, ciphertext } = await encryptString(encKey, rawKey)
    const lastFour = rawKey.slice(-4)

    const record: StoredKeyRecord = {
      provider,
      label,
      lastFour,
      iv: Array.from(iv),
      ciphertext: Array.from(new Uint8Array(ciphertext)),
      salt: Array.from(salt),
    }

    const db = await openDb(this.idbFactory)
    await idbPut(db, record)
    db.close()
  }

  async listKeys(): Promise<KeyMeta[]> {
    const db = await openDb(this.idbFactory)
    const records = await idbGetAll(db)
    db.close()
    return records.map(({ provider, label, lastFour }) => ({ provider, label, lastFour }))
  }

  async getKey(provider: string): Promise<string | null> {
    const db = await openDb(this.idbFactory)
    const record = await idbGet(db, provider)
    db.close()

    if (!record) return null

    const salt = new Uint8Array(record.salt)
    const iv = new Uint8Array(record.iv)
    const ciphertext = new Uint8Array(record.ciphertext).buffer

    const encKey = await deriveKey(this.passphrase, salt)
    return decryptString(encKey, iv, ciphertext)
  }

  async removeKey(provider: string): Promise<void> {
    const db = await openDb(this.idbFactory)
    await idbDelete(db, provider)
    db.close()
  }

  async hasKey(provider: string): Promise<boolean> {
    const db = await openDb(this.idbFactory)
    const record = await idbGet(db, provider)
    db.close()
    return record !== undefined
  }
}

// Derive the device passphrase for the browser context.
// Phase 1: random secret stored in localStorage (device-bound).
// Phase 2: replace with Clerk session token for user-bound encryption.
export function getDevicePassphrase(): string {
  const STORAGE_KEY = 'ivai_device_secret'
  const existing = localStorage.getItem(STORAGE_KEY)
  if (existing !== null) return existing

  const bytes = crypto.getRandomValues(new Uint8Array(32))
  const secret = btoa(bytes.reduce((s, b) => s + String.fromCharCode(b), ''))
  localStorage.setItem(STORAGE_KEY, secret)
  return secret
}
